import React from 'react';
import { useRouter } from 'expo-router';
import { useTranslation } from '@/hooks/useTranslation';
import NavigationBarButtonContainer from '@/components/Navigation/NavigationBarButtonContainer';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import SidebarIcon from '@/components/Icons/SidebarIcon';
import { useTheme } from '@/context/ThemeContext';
import ThemeSelector from '@/components/Navigation/ThemeSelector';
import InfoSelector from '@/components/Navigation/InfoSelector';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useUIStore } from '@/store';
import { useAuthPresentation } from '@/hooks/useRequireAuth';
import { Keyboard, Platform } from 'react-native';
import { useNavigation, DrawerActions } from '@react-navigation/native';

export default function DefaultRouteNavigationHeaderLeft({
  children,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const router = useRouter();
  const toggleSidebar = useUIStore(state => state.toggleSidebar);
  const navigation = useNavigation();
  const { isXS } = useResponsiveness();

  // Match the optimistic Android shell while persisted auth hydrates or its
  // access token refreshes. Otherwise the shell shows signed-in content with
  // the logged-out home/theme/help controls during a cold start.
  const isAuthenticated = useAuthPresentation();
  // Desktop web keeps its toggle beside Settings; the compact rail's logo reopens it.
  // Phones and native drawers still need a header button while the menu is hidden.
  const showSidebarToggle = (isAuthenticated || Platform.OS === 'web') && (Platform.OS !== 'web' || isXS);

  const handleMenuPress = () => {
    if (Platform.OS === 'android' || Platform.OS === 'ios') {
      Keyboard.dismiss();
    }

    if (Platform.OS === 'web' || Platform.OS === 'macos') {
      toggleSidebar();
      return;
    }

    // Prefer dispatching DrawerActions for reliability on native
    try {
      navigation.dispatch(DrawerActions.toggleDrawer());
      return;
    } catch (e) {}

    if (navigation && typeof navigation.openDrawer === 'function') {
      navigation.openDrawer();
      return;
    }

    if (navigation && typeof navigation.toggleDrawer === 'function') {
      navigation.toggleDrawer();
      return;
    }

    // Fallback
    toggleSidebar();
  };

  return (
    <NavigationBarButtonContainer leftPadding>
      {showSidebarToggle && (
        <NavigationBarButton
          icon={<SidebarIcon color={colors.text_color_primary} size={isXS ? 18 : 16} />}
          tooltip={t('nav.routes.defaultHeader.toggleSidebar')}
          tooltipShortcut='⌘ ⇧ S'
          onPress={handleMenuPress}
        />
      )}
      {!isAuthenticated && Platform.OS !== 'web' && (
        <NavigationBarButton
          icon="home"
          title={t('navigation.home')}
          onPress={() => router.push('/')}
        />
      )}
      {/* On web the theme + info controls live in the sidebar header for
          logged-out visitors; native keeps them inline since there's no
          persistent sidebar header. */}
      {!isAuthenticated && Platform.OS !== 'web' && <ThemeSelector showAccentSelector={false} />}
      {!isAuthenticated && Platform.OS !== 'web' && <InfoSelector />}
      {children}
    </NavigationBarButtonContainer>
  );
}
